import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import { CalendarReminder } from '../../../../../../../../backend/src/matter/procedural-timeline/calendar-invite.builder';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { useDownloadProceduralCalendar } from '@/api/mutations/useDownloadProceduralCalendar';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';

const REMINDER_OPTIONS: { value: CalendarReminder; label: string }[] = [
  { value: CalendarReminder.None, label: 'No reminder' },
  { value: CalendarReminder.OneHour, label: '1 hour before' },
  { value: CalendarReminder.OneDay, label: '1 day before' },
  { value: CalendarReminder.OneWeek, label: '1 week before' },
];

interface ExportInviteDialogProps {
  /** Omit to download every dated event on the timeline as a zip. */
  event?: ProceduralTimelineEvent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExportInviteDialog({ event, open, onOpenChange }: ExportInviteDialogProps) {
  const { matterId } = useParams();
  const download = useDownloadProceduralCalendar(matterId);

  const allEvents = event === undefined;
  const hasDate = allEvents || event.startDateTime !== null;
  const [reminder, setReminder] = useState<CalendarReminder>(CalendarReminder.None);

  useEffect(() => {
    if (open) setReminder(CalendarReminder.None);
  }, [open]);

  const handleDownload = () => {
    if (!hasDate) return;
    download.mutate(
      allEvents
        ? { reminder, filename: 'procedural-timeline.zip' }
        : { eventId: event.id, reminder, filename: `${event.name.trim() || 'event'}.ics` },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  const title = allEvents ? 'Download all events' : 'Add to calendar';
  const description = allEvents
    ? 'Download every dated event as a zip of calendar files to import into any calendar.'
    : `Download "${event.name}" as a calendar file to import into any calendar.`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {!hasDate ? (
          <p className="text-sm text-gray-600">
            Nothing to export - add a date to this event to download a calendar file.
          </p>
        ) : (
          <div className="space-y-3 pt-1">
            <div>
              <Label>Reminder</Label>
              <p className="mb-2 text-xs text-gray-500">Add an alert before the event starts.</p>
              <Select value={reminder} onValueChange={(v) => setReminder(v as CalendarReminder)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REMINDER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={download.isLoading}>
            Cancel
          </Button>
          {hasDate && (
            <Button type="button" variant="primary" onClick={handleDownload} disabled={download.isLoading}>
              {download.isLoading ? (
                <Icon className="lucide--loader-2 animate-spin" />
              ) : (
                <Icon className="lucide--download" />
              )}
              {download.isLoading ? 'Preparing…' : 'Download'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
